import { t, useLanguage } from '../../i18n';
import LanguageSwitch from '../../components/LanguageSwitch';
import React, { useState, useEffect, useContext } from 'react';
import { ThemePreferencesContext } from '../../contexts/ThemeContext';
import { useSchedule } from '../../contexts/ScheduleContext';
import { View, StyleSheet, ScrollView, TouchableOpacity, Alert, Linking, DeviceEventEmitter, Platform } from 'react-native';
import { Text, Card, Button, useTheme, List, Divider, Dialog, Portal, TextInput, RadioButton } from 'react-native-paper';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider, signOut } from 'firebase/auth';
import Constants from 'expo-constants';

type SettingsScreenProps = {
  navigation: any;
  route?: any;
};

const YEAR_OPTIONS = [
  { label: '1年', value: '1' },
  { label: '2年', value: '2' },
  { label: '3年', value: '3' },
  { label: '4年', value: '4' },
  { label: '院1年', value: '5' },
  { label: '院2年', value: '6' },
];

const FACULTY_OPTIONS = [
  { label: '言語文化学部', value: 'lc' },
  { label: '国際社会学部', value: 'ia' },
  { label: '国際日本学部', value: 'js' },
  { label: '大学院総合国際学研究科', value: 'pg' },
];

const YEAR_LABEL_MAP: Record<string, string> = Object.fromEntries(
  YEAR_OPTIONS.map((option) => [option.value, option.label])
);

const FACULTY_LABEL_MAP: Record<string, string> = Object.fromEntries(
  FACULTY_OPTIONS.map((option) => [option.value, option.label])
);

const SettingsScreen = ({ navigation, route }: SettingsScreenProps) => {
  useLanguage();
  const { toggleTheme, isDarkMode } = useContext(ThemePreferencesContext);
  const { clearAllCourses } = useSchedule();

  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileYear, setProfileYear] = useState('');
  const [profileFaculty, setProfileFaculty] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const theme = useTheme();

  useEffect(() => {
    fetchUserData();
  }, []);

  // 他画面から「設定へ移動」導線で遷移してきた場合、プロフィール編集ダイアログを自動的に開く
  useEffect(() => {
    if (route?.params?.openProfileDialog) {
      setShowProfileDialog(true);
      navigation.setParams({ openProfileDialog: undefined });
    }
  }, [route?.params?.openProfileDialog]);

  const fetchUserData = async () => {
    if (!auth.currentUser) { setLoading(false); return; }

    try {
      setLoading(true);

      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        const nextUserData = userDoc.data();
        setUserData(nextUserData);
        setNewEmail(auth.currentUser.email || '');
        setProfileYear(nextUserData?.year ? String(nextUserData.year) : '');
        setProfileFaculty(nextUserData?.faculty ? String(nextUserData.faculty) : '');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFacultyChange = (value: string) => {
    setProfileFaculty(value);
    
    // 学部変更に伴い、選択中の学年が適合しなくなる場合はリセットする
    if (value === 'pg') {
      if (profileYear !== '5' && profileYear !== '6') {
        setProfileYear('');
      }
    } else {
      if (profileYear !== '1' && profileYear !== '2' && profileYear !== '3' && profileYear !== '4') {
        setProfileYear('');
      }
    }
  };

  const isYearOptionDisabled = (yearValue: string) => {
    if (!profileFaculty) {
      // 学部未選択のときは全項目を非活性にする
      return true;
    }
    if (profileFaculty === 'pg') {
      // 大学院総合国際学研究科の場合は、院生学年（5, 6）以外を非活性にする
      return yearValue !== '5' && yearValue !== '6';
    } else {
      // それ以外（lc, ia, js）の場合は、学部学年（1, 2, 3, 4）以外を非活性にする
      return yearValue !== '1' && yearValue !== '2' && yearValue !== '3' && yearValue !== '4';
    }
  };

  const handleUpdateProfile = async () => {
    if (!auth.currentUser) {
      Alert.alert(t("エラー"), t("ログインユーザー情報が見つかりません"));
      return;
    }

    const nextFaculty = profileFaculty.trim();
    if (!nextFaculty) {
      Alert.alert(t("入力エラー"), t("学部を選択してください"));
      return;
    }

    if (!profileYear) {
      Alert.alert(t("入力エラー"), t("学年を選択してください"));
      return;
    }

    const parsedYear = Number(profileYear);
    if (nextFaculty === 'pg') {
      if (![5, 6].includes(parsedYear)) {
        Alert.alert(t("入力エラー"), t("大学院総合国際学研究科では、院1年〜院2年を選択してください"));
        return;
      }
    } else {
      if (![1, 2, 3, 4].includes(parsedYear)) {
        Alert.alert(t("入力エラー"), t("学部生は、1〜4年を選択してください"));
        return;
      }
    }

    try {
      setSavingProfile(true);
      await setDoc(doc(db, 'users', auth.currentUser.uid), {
        year: parsedYear,
        faculty: nextFaculty,
      }, { merge: true });

      setUserData((prev: any) => ({
        ...(prev || {}),
        year: parsedYear,
        faculty: nextFaculty,
      }));

      setShowProfileDialog(false);
      await fetchUserData();
    } catch (error) {
      console.error('Error updating profile:', error);
      Alert.alert(t("エラー"), t("プロフィール更新に失敗しました"));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSignOut = async () => {
    console.log('handleSignOut triggered');
    setShowLogoutDialog(true);
  };

  const performSignOut = async () => {
    try {
      console.log('Performing sign out...');
      DeviceEventEmitter.emit('auth_guest_change', false);
      await signOut(auth);
      setShowLogoutDialog(false);
    } catch (error) {
      console.error('Error signing out:', error);
      Alert.alert(t("エラー"), t("ログアウトに失敗しました"));
    }
  };

  const handleUpdatePassword = async () => {
    if (!auth.currentUser || !auth.currentUser.email) {
      Alert.alert(t("エラー"), t("ユーザー情報が取得できません"));
      return;
    }

    if (newPassword !== confirmPassword) {
      Alert.alert(t("エラー"), t("新しいパスワードと確認用パスワードが一致しません"));
      return;
    }

    if (newPassword.length < 8) {
      Alert.alert(t("エラー"), t("パスワードは8文字以上である必要があります"));
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword
      );

      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPassword);

      Alert.alert(t("成功"), t("パスワードが更新されました"));
      setShowPasswordDialog(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      Alert.alert(t("エラー"), t("パスワードの更新に失敗しました。現在のパスワードが正しいか確認してください"));
    }
  };

  const handleUpdateEmail = async () => {
    if (!auth.currentUser || !auth.currentUser.email) {
      Alert.alert(t("エラー"), t("ユーザー情報が取得できません"));
      return;
    }

    if (!newEmail || !newEmail.includes('@')) {
      Alert.alert(t("エラー"), t("有効なメールアドレスを入力してください"));
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword
      );

      await reauthenticateWithCredential(auth.currentUser, credential);
      await updateEmail(auth.currentUser, newEmail);

      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        email: newEmail,
      });

      Alert.alert(t("成功"), t("メールアドレスが更新されました"));
      setShowEmailDialog(false);
      setCurrentPassword('');
      fetchUserData();
    } catch (error) {
      console.error('Error updating email:', error);
      Alert.alert(t("エラー"), t("メールアドレスの更新に失敗しました。現在のパスワードが正しいか確認してください"));
    }
  };

  const goToLogin = () => {
    DeviceEventEmitter.emit('auth_guest_change', false);
  };

  const getAppVersion = () => {
    return Constants.expoConfig?.version || '1.0.0';
  };

  const displayFaculty = userData?.faculty
    ? FACULTY_LABEL_MAP[String(userData.faculty)] || String(userData.faculty)
    : '-';

  const displayYear = userData?.year
    ? YEAR_LABEL_MAP[String(userData.year)] || `${userData.year}年`
    : '-';

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <View style={styles.headerTextGroup}>
          <Text style={styles.headerTitle}>{t("設定")}</Text>
          {auth.currentUser?.email ? (
            <Text style={styles.headerEmail} numberOfLines={1}>
              {auth.currentUser.email}
            </Text>
          ) : null}
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.primary }]}>{t('言語 / Language')}</Text>
            <LanguageSwitch />
          </Card.Content>
        </Card>
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.primary }]}>
              {t('カレンダー連携')}
            </Text>
            <List.Item
              title={t('Googleカレンダー')}
              description={t('時間割の授業を毎週の予定として追加')}
              descriptionNumberOfLines={2}
              accessibilityRole="button"
              accessibilityLabel={t('Googleカレンダーに追加')}
              titleStyle={{ fontWeight: '600' }}
              left={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="google" size={24} color={theme.colors.primary} />} />}
              right={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="chevron-right" size={24} color={props.color} />} />}
              onPress={() => navigation.navigate('CalendarExport')}
            />
          </Card.Content>
        </Card>
        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.primary }]}>
              {t("アカウント設定")}
            </Text>

            {auth.currentUser ? (
              <>
                <List.Item
                  title={t("メールアドレス")}
                  description={auth.currentUser?.email || ''}
                  left={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="email" size={24} color={props.color} />} />}
                  right={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="chevron-right" size={24} color={props.color} />} />}
                  onPress={() => setShowEmailDialog(true)}
                />

                <Divider />

                <List.Item
                  title={t("パスワード変更")}
                  description={t("セキュリティのため定期的に変更してください")}
                  left={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="lock" size={24} color={props.color} />} />}
                  right={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="chevron-right" size={24} color={props.color} />} />}
                  onPress={() => setShowPasswordDialog(true)}
                />

                <Divider />

                <List.Item
                  title={t("プロフィール編集")}
                  description={t("学年: {0} / 学部: {1}", [t(displayYear), t(displayFaculty)])}
                  left={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="account" size={24} color={props.color} />} />}
                  right={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="chevron-right" size={24} color={props.color} />} />}
                  onPress={() => setShowProfileDialog(true)}
                />
              </>
            ) : (
              <View style={styles.guestContainer}>
                <Text style={styles.guestText}>
                  {t("現在はゲストモードで利用中です")}
                </Text>
                <Button
                  mode="contained"
                  onPress={goToLogin}
                  style={styles.loginButton}
                  icon={() => <MaterialCommunityIcons name="login" size={20} color="white" />}
                >
                  {t("ログイン / 新規登録")}
                </Button>
              </View>
            )}
          </Card.Content>
        </Card>

        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.primary }]}>
              {t("表示設定")}
            </Text>

            <List.Item
              title={t("テーマ")}
              description={isDarkMode ? t("ダークモード") : t("ライトモード")}
              left={(props) => (
                <List.Icon
                  {...props}
                  icon={() => <MaterialCommunityIcons name={isDarkMode ? 'weather-night' : 'weather-sunny'} size={24} color={isDarkMode ? '#bb86fc' : '#ffb300'} />}
                />
              )}
              right={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="chevron-right" size={24} color={props.color} />} />}
              onPress={toggleTheme}
            />
          </Card.Content>
        </Card>

        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.primary }]}>
              {t("データ管理")}
            </Text>

            <List.Item
              title={t("時間割をリセット")}
              description={t("現在の時間割をすべて削除します")}
              left={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="delete-sweep" size={24} color={theme.colors.error} />} />}
              right={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="chevron-right" size={24} color={props.color} />} />}
              onPress={() => {
                const performReset = async () => {
                  try {
                    await clearAllCourses();
                    if (Platform.OS === 'web') {
                      window.alert(t("時間割をリセットしました"));
                    } else {
                      Alert.alert(t("完了"), t("時間割をリセットしました"));
                    }
                    navigation.navigate('Schedule');
                  } catch (e) {
                    console.error('Reset failed:', e);
                  }
                };

                if (Platform.OS === 'web') {
                  const confirmReset = window.confirm(t("現在の時間割をすべて削除してもよろしいですか？この操作は取り消せません。"));
                  if (confirmReset) {
                    performReset();
                  }
                } else {
                  Alert.alert(
                    t("時間割をリセット"),
                    t("現在の時間割をすべて削除してもよろしいですか？この操作は取り消せません。"),
                    [
                      { text: t("キャンセル"), style: 'cancel' },
                      {
                        text: t("リセット"),
                        style: 'destructive',
                        onPress: performReset
                      }
                    ]
                  );
                }
              }}
            />
          </Card.Content>
        </Card>

        <Card style={[styles.card, { backgroundColor: theme.colors.surface }]}>
          <Card.Content>
            <Text style={[styles.sectionTitle, { color: theme.colors.primary }]}>
              {t("アプリ情報")}
            </Text>

            <List.Item
              title={t("アプリバージョン")}
              description={getAppVersion()}
              left={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="information" size={24} color={props.color} />} />}
            />



            <Divider />

            <List.Item
              title={t("お問い合わせフォーム")}
              left={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="email-send" size={24} color={props.color} />} />}
              right={(props) => <List.Icon {...props} icon={() => <MaterialCommunityIcons name="chevron-right" size={24} color={props.color} />} />}
              onPress={() => {
                Linking.openURL('https://forms.office.com/Pages/ResponsePage.aspx?id=DQSIkWdsW0yxEjajBLZtrQAAAAAAAAAAAANAAUC6IdpUNlM5M0JUTFdZM0s1TkpCN0xEUEg2QVRNQy4u');
              }}
            />
          </Card.Content>
        </Card>

        <Button
          mode="outlined"
          onPress={handleSignOut}
          style={[styles.signOutButton, { borderColor: theme.colors.error }]}
          textColor={theme.colors.error}
        >
          {auth.currentUser ? t("ログアウト") : t("ログイン・登録へ戻る")}
        </Button>

        <View style={styles.footer}>
          <Text style={[styles.footerText, { color: theme.colors.onBackground, opacity: 0.7 }]}>
            {t("TUFS時間割 © 2025")}
          </Text>
          <Text style={[styles.disclaimerText, { color: theme.colors.onBackground, opacity: 0.6 }]}>
            {t("注意: 保存したデータが消えたり、書き換わったり、閲覧できなくなる可能性があります。 また公開されている情報を元に作成した授業情報は古く、正しくない可能性があります。 必ず公式サイトにて最新情報をご確認ください。 このアプリの使用において発生した問題について、一切製作者は責任を負いません。 上記全てに了承の上インストールしてください。")}
          </Text>
        </View>
      </ScrollView>

      <Portal>
        <Dialog
          visible={showPasswordDialog}
          onDismiss={() => setShowPasswordDialog(false)}
          style={{ backgroundColor: theme.colors.surface }}
        >
          <Dialog.Title>{t("パスワード変更")}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t("現在のパスワード")}
              value={currentPassword}
              onChangeText={setCurrentPassword}
              secureTextEntry={!passwordVisible}
              right={
                <TextInput.Icon
                  icon={() => <MaterialCommunityIcons name={passwordVisible ? 'eye-off' : 'eye'} size={24} color={theme.colors.onSurface} />}
                  onPress={() => setPasswordVisible(!passwordVisible)}
                />
              }
              style={styles.dialogInput}
            />
            <TextInput
              label={t("新しいパスワード")}
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry={!passwordVisible}
              style={styles.dialogInput}
            />
            <TextInput
              label={t("新しいパスワード（確認）")}
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry={!passwordVisible}
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowPasswordDialog(false)}>{t("キャンセル")}</Button>
            <Button onPress={handleUpdatePassword}>{t("更新")}</Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog
          visible={showEmailDialog}
          onDismiss={() => setShowEmailDialog(false)}
          style={{ backgroundColor: theme.colors.surface }}
        >
          <Dialog.Title>{t("メールアドレス変更")}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t("新しいメールアドレス")}
              value={newEmail}
              onChangeText={setNewEmail}
              keyboardType="email-address"
              style={styles.dialogInput}
            />
            <TextInput
              label={t("現在のパスワード")}
              value={currentPassword}
              onChangeText={setCurrentPassword}
              secureTextEntry={!passwordVisible}
              right={
                <TextInput.Icon
                  icon={() => <MaterialCommunityIcons name={passwordVisible ? 'eye-off' : 'eye'} size={24} color={theme.colors.onSurface} />}
                  onPress={() => setPasswordVisible(!passwordVisible)}
                />
              }
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowEmailDialog(false)}>{t("キャンセル")}</Button>
            <Button onPress={handleUpdateEmail}>{t("更新")}</Button>
          </Dialog.Actions>
        </Dialog>
        <Dialog
          visible={showLogoutDialog}
          onDismiss={() => setShowLogoutDialog(false)}
          style={{ backgroundColor: theme.colors.surface }}
        >
          <Dialog.Title>{t("ログアウト")}</Dialog.Title>
          <Dialog.Content>
            <Text>{t("ログアウトしてもよろしいですか？")}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowLogoutDialog(false)}>{t("キャンセル")}</Button>
            <Button onPress={performSignOut} textColor={theme.colors.error}>{t("ログアウト")}</Button>
          </Dialog.Actions>
        </Dialog>

        <Dialog
          visible={showProfileDialog}
          onDismiss={() => setShowProfileDialog(false)}
          style={{ backgroundColor: theme.colors.surface }}
        >
          <Dialog.Title>{t("プロフィール編集")}</Dialog.Title>
          <Dialog.Content>
            <ScrollView style={{ maxHeight: 300 }}>
              <Text style={{ marginBottom: 8, fontWeight: 'bold' }}>{t("学部")}</Text>
              <RadioButton.Group onValueChange={handleFacultyChange} value={profileFaculty}>
                {FACULTY_OPTIONS.map((option) => (
                  <RadioButton.Item key={option.value} label={t(option.label)} value={option.value} />
                ))}
              </RadioButton.Group>
              <Divider style={{ marginVertical: 16 }} />
              <Text style={{ marginBottom: 8, fontWeight: 'bold' }}>{t("学年")}</Text>
              <RadioButton.Group onValueChange={setProfileYear} value={profileYear}>
                {YEAR_OPTIONS.map((option) => (
                  <RadioButton.Item 
                    key={option.value} 
                    label={t(option.label)}
                    value={option.value} 
                    disabled={isYearOptionDisabled(option.value)}
                  />
                ))}
              </RadioButton.Group>
            </ScrollView>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setShowProfileDialog(false)}>{t("キャンセル")}</Button>
            <Button onPress={handleUpdateProfile} loading={savingProfile} disabled={savingProfile}>{t("保存")}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    backgroundColor: '#e75480',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  headerTextGroup: {
    flex: 1,
    justifyContent: 'center',
  },
  headerEmail: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 2,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  card: {
    marginBottom: 16,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  signOutButton: {
    marginTop: 8,
    marginBottom: 24,
  },
  footer: {
    alignItems: 'center',
    marginTop: 8,
  },
  footerText: {
    fontSize: 14,
    marginBottom: 8,
  },
  disclaimerText: {
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 18,
    paddingHorizontal: 16,
  },
  dialogInput: {
    marginBottom: 16,
  },
  guestContainer: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  guestText: {
    marginBottom: 16,
    opacity: 0.7,
  },
  loginButton: {
    width: '100%',
  },
});

export default SettingsScreen;
